/*  MUI  */
import { AssignmentTurnedIn, RestorePage } from '@mui/icons-material';
import { Alert, Button, Grid, Paper, TableCell } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Helpers, Notify } from 'application/Shared/Helpers';
import Cards from 'application/Shared/Pages/Notes/Components/Cards';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import { Application } from 'objects/Application';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Constants  */
const endpoints = {
    client: '/note',
    completed: '/completedNote'
};

const headers = [
    {
        renderHeader: i18n.t('Common.application'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        width: '25%'
    },
    {
        renderHeader: i18n.t('Common.type'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'type',
        width: '11%'
    },
    {
        renderHeader: i18n.t('Common.note/recommendation'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'message',
        width: '25%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        sortable: false,
        width: '25%'
    }
];

const parameters = {
    applicationId: '*',
    excludeCompleted: true,
    includeAll: true,
    includeApp: true,
    includeOnlyThisTenant: true,
    size: -1
};

const searchFields = ['id', 'type', 'message', 'application.name'];

const toggles = [
    {
        defaultEnabled: false,
        key: 'completed',
        parameter: { field: 'includeCompleted', value: true },
        toggleText: i18n.t('Notes.showCompletedNotes')
    }
];

/*  State  */
const state = () => ({
    cardParams: { applicationId: '*', includeCompleted: true },
    loaded: false,
    noResults: true,
    renderTimer: false,
    rerender: false,
    tableLoaded: false
});

class Dashboard extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        this.timeout = 0;
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate() {
        const { reload } = this.props;
        if (reload) {
            this.reload();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
        this.unload();
    }

    load = () => {
        this.setState({ loaded: true });
        this.timeout = setTimeout(() => {
            if (!this.unmounted) {
                this.setState({ renderTimer: true });
            }
        }, 1000);
    };

    reload = () => {
        this.setState(state(), () => this.load());
    };

    rerender = () => {
        this.setState({ rerender: true }, () => {
            this.setState({ rerender: false });
        });
    };

    unload = () => {
        this.setState(state());
    };

    complete = async (row) => {
        const { match } = this.props;
        const { clientId } = match.params;
        const { id, partner, community } = row;

        debug.i('Completing App Note', row);

        let type = 'client';
        if (partner) {
            type = 'partner';
        }
        if (community) {
            type = 'community';
        }

        const params = { noteId: id, type };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await axios.post(endpoints.completed, params);
            Notify.i('Note has been marked as completed.');
            this.rerender();
        } catch (e) {
            debug.e('Unable to mark note as completed', e);
            Notify.e('Unable to mark note as completed.');
        }
    };

    restore = async (row) => {
        const { id } = row;
        debug.i('Restoring completed note', row);

        try {
            await axios.delete(`${endpoints.completed}/${id}`);
            Notify.i('Note has been restored.');
            this.rerender();
        } catch (e) {
            debug.e('Unable to restore note.', e);
            Notify.e('Unable to restore note.');
        }
    };

    handleRows = (rows) => {
        const { noResults } = this.state;
        if (!noResults) {
            return;
        }

        if (Helpers.count(rows)) {
            this.setState({ noResults: false });
        }

        this.setState({ tableLoaded: true });
    };

    renderActions = (row) => {
        const { t } = this.props;
        const { completed } = row;

        if (!completed) {
            return (
                <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    style={{ marginRight: '4px' }}
                    onClick={() => this.complete(row)}
                    data-trackid="admin_notes_markAsComplete"
                >
                    <AssignmentTurnedIn style={{ marginRight: '4px' }} />
                    {t('Notes.mark_as_complete')}
                </Button>
            );
        }

        return (
            <Button
                size="small"
                variant="outlined"
                style={{ marginRight: '4px' }}
                onClick={() => this.restore(row)}
                data-trackid="admin_notes_restore"
            >
                <RestorePage style={{ marginRight: '4px' }} />
                {t('Common.restore')}
            </Button>
        );
    };

    renderRow = (row) => {
        const { application, type, message } = row;
        if (!application) {
            return null;
        }

        return (
            <>
                <TableCell>
                    <Application data={application} nameHasLink>
                        <ApplicationNameLinkCell withCategory />
                    </Application>
                </TableCell>
                <TableCell>{type}</TableCell>
                <TableCell>{message}</TableCell>
                <TableCell align="right">{this.renderActions(row)}</TableCell>
            </>
        );
    };

    renderTable = () => {
        const { rerender } = this.state;

        const rowsApi = endpoints.client;

        return (
            <ManagedTable
                handleRows={this.handleRows}
                headers={headers}
                parameters={parameters}
                renderBody={this.renderRow}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rowsApi={rowsApi}
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="application.name"
                toggles={toggles}
            />
        );
    };

    renderNoResults = () => {
        const { t } = this.props;
        const { tableLoaded, noResults, renderTimer } = this.state;
        if (!tableLoaded || !noResults) {
            return null;
        }

        if (!renderTimer) {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Alert severity="warning" icon={false}>
                    {t('Admin.Notes.no_results')}
                </Alert>
            </Grid>
        );
    };

    render() {
        const { t } = this.props;
        const { loaded, cardParams } = this.state;

        return (
            <>
                <PageTitle title={t('Common.notes_and_recommendations')} />
                <Alert
                    severity="info"
                    icon={false}
                    sx={{ marginBottom: '16px' }}
                >
                    {t('Admin.Notes.header_notice')}
                </Alert>
                {loaded && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Cards params={cardParams} />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={0} variant="outlined">
                                <Grid item xs={12}>
                                    {this.renderNoResults()}
                                    {this.renderTable()}
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </>
        );
    }
}

export default withRouter(withTranslation()(Dashboard));
